/**
 * API Request Manager
 * Prevents duplicate requests and handles rate limiting with exponential backoff
 */

interface PendingRequest<T> {
  promise: Promise<T>;
  timestamp: number;
}

class ApiRequestManager {
  private static instance: ApiRequestManager;
  private pendingRequests = new Map<string, PendingRequest<any>>();
  private rateLimitBackoff = new Map<string, number>();
  
  static getInstance(): ApiRequestManager {
    if (!ApiRequestManager.instance) {
      ApiRequestManager.instance = new ApiRequestManager();
    }
    return ApiRequestManager.instance;
  }

  /**
   * Execute request with deduplication and rate limit handling
   */
  async executeRequest<T>(
    key: string,
    requestFn: () => Promise<T>,
    options: {
      dedupeDuration?: number; // How long to dedupe requests (ms)
      maxRetries?: number;
      baseDelay?: number; // Base delay for exponential backoff
    } = {}
  ): Promise<T> {
    const {
      dedupeDuration = 1000, // 1 second default
      maxRetries = 3,
      baseDelay = 1000
    } = options;

    // Check if we're in a rate limit backoff period
    const backoffUntil = this.rateLimitBackoff.get(key);
    if (backoffUntil && Date.now() < backoffUntil) {
      const waitTime = backoffUntil - Date.now();
      console.log(`⏳ Rate limit backoff for ${key}, waiting ${waitTime}ms`);
      await new Promise(resolve => setTimeout(resolve, waitTime));
    }

    // Check for existing pending request
    const pending = this.pendingRequests.get(key);
    if (pending && (Date.now() - pending.timestamp) < dedupeDuration) {
      console.log(`🔄 Deduplicating request for ${key}`);
      return pending.promise;
    }

    // Create new request with retry logic
    const requestPromise = this.executeWithRetry(key, requestFn, maxRetries, baseDelay);
    
    // Store pending request
    this.pendingRequests.set(key, {
      promise: requestPromise,
      timestamp: Date.now()
    });

    try {
      const result = await requestPromise;
      
      // Clear successful request
      this.pendingRequests.delete(key);
      this.rateLimitBackoff.delete(key);
      
      return result;
    } catch (error) {
      // Clear failed request
      this.pendingRequests.delete(key);
      throw error;
    }
  }

  private async executeWithRetry<T>(
    key: string,
    requestFn: () => Promise<T>,
    maxRetries: number,
    baseDelay: number
  ): Promise<T> {
    let lastError: any;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        return await requestFn();
      } catch (error: any) {
        lastError = error;
        
        // Handle rate limiting (429)
        if (error.response?.status === 429) {
          const delay = this.calculateBackoffDelay(attempt, baseDelay);
          const backoffUntil = Date.now() + delay;
          
          this.rateLimitBackoff.set(key, backoffUntil);
          console.warn(`⚠️ Rate limited for ${key}, backing off for ${delay}ms`);
          
          if (attempt < maxRetries) {
            await new Promise(resolve => setTimeout(resolve, delay));
            continue;
          }
        }
        
        // Handle server errors (5xx) with retry
        if (error.response?.status >= 500 && attempt < maxRetries) {
          const delay = this.calculateBackoffDelay(attempt, baseDelay);
          console.warn(`🔧 Server error for ${key}, retrying in ${delay}ms`);
          await new Promise(resolve => setTimeout(resolve, delay));
          continue;
        }
        
        // For other errors or max retries reached, throw immediately
        break;
      }
    }

    throw lastError;
  }

  private calculateBackoffDelay(attempt: number, baseDelay: number): number {
    // Exponential backoff with jitter
    const exponentialDelay = baseDelay * Math.pow(2, attempt);
    const jitter = Math.random() * 1000; // Add up to 1s jitter
    return Math.min(exponentialDelay + jitter, 10000); // Reduced from 30000ms to 10000ms
  }

  /**
   * Clear all pending requests and backoffs
   */
  reset(): void {
    this.pendingRequests.clear();
    this.rateLimitBackoff.clear();
    console.log('🗑️ Cleared all API request state');
  }

  /**
   * Get current backoff status for debugging
   */
  getBackoffStatus(): Record<string, any> {
    const status: Record<string, any> = {};
    const now = Date.now();

    this.rateLimitBackoff.forEach((backoffUntil, key) => {
      status[key] = {
        isBackedOff: backoffUntil > now,
        remainingMs: Math.max(0, backoffUntil - now)
      };
    });

    return status;
  }

  /**
   * Get pending requests for debugging
   */
  getPendingRequests(): string[] {
    return Array.from(this.pendingRequests.keys());
  }
}

// Export singleton
export const apiRequestManager = ApiRequestManager.getInstance(); 